import { ErrorCodeType } from "application/models/IError";
import {
  PrintOrderType,
  SpoolerStatus,
} from "components/layoutsPrinter/IPrintOrder";
import LayoutPrinter, {
  ILayoutPrinterHandler,
} from "components/layoutsPrinter/LayoutPrinter";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { PrintStatus } from "domains/order/agreggates/order/Order";
import { IPrintRepository } from "domains/order/interfaces/repositories/iprintRespository";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { LocalPrintRepository } from "Infrastructure/services/cefSharpService/local/_LocalPrintRepository";
import { IOrderLog } from "modules/order/domain/models/IOrderLog";
import { localPrintUseCase } from "modules/print/application/useCases/LocalPrintUseCase";
import React, { createRef, FC, useCallback, useEffect, useState } from "react";
import { GetError } from "utils/GetError";
import { useShallow } from "zustand/react/shallow";
import { PrinterStore, usePrinterStore } from "./PrinterStore";
import { getOrderRepository } from "Infrastructure/repositories/localStorage/getOrderRepository";
import { ReactNativePrintRepository } from "Infrastructure/services/reactNativeServices/ReactNativePrinterService";
import { isReactNative, PRINT_ERROR_MODAL } from "utils/constants";
import { PrintErrorToast } from "components/printErrorToast/PrintErrorToast";
import { Operator } from "domains/admin/aggregates/operator/Operator";
import { OperatorRepositoryDB } from "Infrastructure/repositories/localStorage/OperatorRepositoryDB";

const orderRepository = getOrderRepository();
const operatorRepositoryDB = OperatorRepositoryDB();

export interface IPrinterProviderProps {
  kiosk: Kiosk | null;
}

export const layoutPrinterRef = createRef<ILayoutPrinterHandler>();
const NewPrinterProvider: FC<IPrinterProviderProps> = ({ kiosk }) => {
  const printRepository: IPrintRepository = isReactNative ? ReactNativePrintRepository() : LocalPrintRepository();
  const { addLog } = useAppInsights();
  const [operators, setOperators] = useState<Operator[]>([]);

  useEffect(() => {
    if (!kiosk) return;
    operatorRepositoryDB.getAll(kiosk.localId).then((ops) => {
      setOperators(ops);
    });
  }, [kiosk]);

  const [
    setSpoolerStatus,
    spoolerStatus,
    removeOrder,
    getOrderToPrint,
    ordersToPrint,
    tryStartPrinting,
    showPrintError,
    errorMessage,
    setPrintError,
    retryFailedPrint,
    skipFailedPrint,
    failedOrder,
  ] = usePrinterStore(
    useShallow((state: PrinterStore) => [
      state.setSpoolerStatus,
      state.spoolerStatus,
      state.removeOrder,
      state.getOrderToPrint,
      state.ordersToPrint,
      state.tryStartPrinting,
      state.showPrintError,
      state.errorMessage,
      state.setPrintError,
      state.retryFailedPrint,
      state.skipFailedPrint,
      state.failedOrder,
    ])
  );

  // Ficha atual (a que falhou) e a próxima do mesmo pedido, se houver, pra
  // exibir o seletor de ficha no PrintErrorToast.
  const getFichaInfo = useCallback((order: typeof failedOrder) => {
    const data = order?.data as any;
    if (!order || data?.index == null || !data?.product || !data?.products) {
      return null;
    }
    return {
      label: data.product.name as string,
      index: data.index as number,
      total: data.products.length as number,
    };
  }, []);

  const currentFicha = getFichaInfo(failedOrder);
  const nextCandidate = ordersToPrint[1];
  const nextFicha =
    failedOrder && nextCandidate && nextCandidate.orderId === failedOrder.orderId
      ? getFichaInfo(nextCandidate)
      : null;

  const addOrderLog = useCallback(
    async (
      order: IOrderLog | null,
      message: string,
      printStatus: PrintStatus,
      error?: any
    ) => {
      try {
        if (order) {
          let orderLog = {
            ...order,
            printStatus: PrintStatus[printStatus],
            error,
          };
          addLog("PrinterProvider", { message, orderLog });
          // Só o Android grava o status no histórico. O CefSharp devolve
          // IPrintResult (objeto), e as verificações do LocalPrintUseCase são
          // `if (!printResult)` — nunca `printResult.Success` —, então falha de
          // impressora passa como sucesso no Windows: gravar aqui marcaria tudo
          // como SUCCESS e tiraria o botão de reimpressão do OrderLogItemV3
          // (`isPrintClickable`). Origem: b55a0159 (15/05/25) desligou a escrita
          // porque "alguma impressora não possui controle de impressão";
          // a5bcd807 (15/06/26) religou só no Android, junto com a tela V3.
          // Para remover este if: corrigir as checagens de `Success` no
          // LocalPrintUseCase e trocar a condição por `kiosk.newPrinter`, que é
          // a regra real (impressora com retorno confiável), não a plataforma.
          if (isReactNative) {
            await orderRepository.add(order, message, undefined, printStatus);
          }
        }
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "NewPrinterProvider",
          "Falha ao adicionar log",
          ErrorCodeType.PRINT
        );
      }
    },
    [addLog]
  );

  const printOrderHandler = useCallback(
    async (_kiosk: Kiosk) => {
      const orderToPrint = getOrderToPrint();
      try {
        //throw new Error("Erro de teste"); //TODO: Remover
        console.log("printOrderHandler", orderToPrint);
        await localPrintUseCase(
          layoutPrinterRef,
          printRepository,
          orderToPrint,
          _kiosk
        );
        console.log("printOrderHandler after", orderToPrint);
        if (orderToPrint.type === PrintOrderType.ORDER) {
          await addOrderLog(
            {
              id: orderToPrint.orderId,
              ticketId: orderToPrint.data?.product?.ticketId,
            } as IOrderLog,
            `${
              orderToPrint.data?.isReprint
                ? "Reimpressão completa"
                : "Impressão completa"
            } ${
              orderToPrint.data?.product?.ticketId
                ? `: Ticket ${orderToPrint.data.index + 1}`
                : ""
            }`,
            PrintStatus.SUCCESS
          );
        }
        removeOrder(orderToPrint.id);
        setSpoolerStatus(SpoolerStatus.standby);
      } catch (error) {
        await addOrderLog(
          {
            id: orderToPrint.orderId,
            ticketId: orderToPrint.data?.product?.ticketId,
          } as IOrderLog,
          `Falha na impressao`,
          PrintStatus.ERROR,
          error
        );
        
        // Se a flag PRINT_ERROR_MODAL estiver habilitada, exibe o toast e pausa o spooler
        if (PRINT_ERROR_MODAL) {
          setPrintError(
            true, 
            "Erro na impressão. Por favor, solicite suporte.", 
            orderToPrint
          );
        } else {
          // Modo antigo: remove o pedido e continua
          removeOrder(orderToPrint.id);
          setSpoolerStatus(SpoolerStatus.standby);
        }
      }
    },
    [
      getOrderToPrint,
      printRepository,
      setSpoolerStatus,
      addOrderLog,
      setPrintError,
      removeOrder,
    ]
  );

  const handleRetryPrint = useCallback(() => {
    retryFailedPrint();
  }, [retryFailedPrint]);

  const handleSkipPrint = useCallback(() => {
    skipFailedPrint();
  }, [skipFailedPrint]);

  useEffect(() => {
    addLog("PrinterProvider", {
      message: "Spooler",
      ordersToPrint,
      status: SpoolerStatus[spoolerStatus],
      showPrintError,
    });
    
    // Função async dentro do useEffect para processar impressão com await
    const processPrint = async () => {
      // Se PRINT_ERROR_MODAL estiver ativo e houver erro sendo exibido, não processa próximas impressões
      if (PRINT_ERROR_MODAL && showPrintError) {
        addLog("PrinterProvider", {
          message: "Aguardando ação do usuário no modal de erro",
          showPrintError,
        });
        return;
      }

      // Usa função atômica para garantir que apenas uma impressão seja iniciada por vez
      // tryStartPrinting só retorna true se conseguir mudar o status de standby para isPrinting
      // Isso previne race conditions e sobreposição de impressões
      if (
        kiosk &&
        !kiosk.barCodeMode && // Modo exclusivo barcode não imprime
        tryStartPrinting() // Verificação e atualização atômicas
      ) {
        await printOrderHandler(kiosk); // Aguarda a conclusão da impressão
      }
    };

    processPrint();
    addLog("PrinterProvider", {
      message: "Spooler",
      ordersToPrint,
      status: SpoolerStatus[spoolerStatus],
      showPrintError,
    });
    //if (
    //  ordersToPrint.length > 0 &&
    //  spoolerStatus === SpoolerStatus.standby &&
    //  kiosk
    //) {
    //  setSpoolerStatus(SpoolerStatus.isPrinting);
    //  printOrderHandler(kiosk);
    //} //TODO: Verificar se é necessário
  }, [
    kiosk,
    ordersToPrint,
    printOrderHandler,
    tryStartPrinting,
    spoolerStatus,
    addLog,
    showPrintError,
  ]);

  if (!ordersToPrint || ordersToPrint.length === 0) {
    // Renderiza apenas o toast de erro se necessário, mesmo sem pedidos na fila
    return PRINT_ERROR_MODAL ? (
      <PrintErrorToast
        isOpen={showPrintError}
        onRetry={handleRetryPrint}
        onSkip={handleSkipPrint}
        message={errorMessage}
        operators={operators}
        currentFicha={currentFicha}
        nextFicha={nextFicha}
      />
    ) : null;
  }

  return (
    <>
      {/* <LayoutPrinterViewer ref={layoutViewerRef} /> */}
      <LayoutPrinter ref={layoutPrinterRef} />
      {PRINT_ERROR_MODAL && (
        <PrintErrorToast
          isOpen={showPrintError}
          onRetry={handleRetryPrint}
          onSkip={handleSkipPrint}
          message={errorMessage}
          operators={operators}
          currentFicha={currentFicha}
          nextFicha={nextFicha}
        />
      )}
    </>
  );
};

export default NewPrinterProvider;
